import { useState } from 'react';
import type { ExportFile } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { Upload } from '@ui/shared/icons';
import { formatShortDate } from '@ui/shared/format/date';

type Stage =
  | { readonly kind: 'idle' }
  | { readonly kind: 'invalid' }
  | { readonly kind: 'preview'; readonly file: ExportFile }
  | { readonly kind: 'importing'; readonly file: ExportFile }
  | { readonly kind: 'done'; readonly imported: number };

/**
 * Import — the app's one destructive-action path (settings-rewards.md §5,
 * phase-2e-nav-settings-export.md §4.3 IM-1..IM-5, AC-N12..AC-N16). File
 * picked, then validated, then previewed — nothing changes until the user
 * types the exact word "replace" and confirms. `importData()` REPLACES
 * local state (architect D2 ruling, ADR-0004 addendum 2026-07-03) — this
 * component calls it exactly once, at the confirm step, never speculatively.
 */
export function ImportFlow() {
  const api = useDomainApi();
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  const [currentCount, setCurrentCount] = useState<number | null>(null);
  const [confirmText, setConfirmText] = useState('');

  async function handleFilePicked(fileList: FileList | null) {
    const picked = fileList?.[0];
    if (!picked) return;

    let parsed: unknown;
    try {
      const text = await readFileAsText(picked);
      parsed = JSON.parse(text);
    } catch {
      setStage({ kind: 'invalid' });
      return;
    }

    if (!isExportFile(parsed)) {
      setStage({ kind: 'invalid' });
      return;
    }

    // Live current-local-count for the confirm step (IM-3: "queried live...
    // not re-deriving a new one") -- exportData() is the existing R5.2
    // data-ownership query, the same one ExportSection uses; reading it here
    // does not trigger a download, it only reads the current local log.
    const current = await api.exportData();
    setCurrentCount(current.events.length);
    setStage({ kind: 'preview', file: parsed });
    setConfirmText('');
  }

  async function handleConfirmedImport() {
    if (stage.kind !== 'preview') return;
    setStage({ kind: 'importing', file: stage.file });
    const summary = await api.importData(stage.file);
    setStage({ kind: 'done', imported: summary.imported });
  }

  function reset() {
    setStage({ kind: 'idle' });
    setConfirmText('');
  }

  return (
    <div>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          minHeight: 'var(--touch-target-min)',
          padding: '0 var(--space-5)',
          borderRadius: 'var(--radius-md)',
          border: '1px solid var(--border-strong)',
          color: 'var(--text-primary)',
          fontWeight: 600,
          fontSize: 'var(--type-body-size)',
          cursor: 'pointer',
        }}
      >
        <Upload size={16} aria-hidden="true" />
        Import from file
        <input
          type="file"
          accept=".json,application/json"
          onChange={(e) => void handleFilePicked(e.target.files)}
          style={{
            position: 'absolute',
            width: '1px',
            height: '1px',
            overflow: 'hidden',
            clip: 'rect(0 0 0 0)',
          }}
        />
      </label>

      {stage.kind === 'invalid' && (
        <p role="status" style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          This file doesn&apos;t look like a Compass export. Nothing was changed.
        </p>
      )}

      {stage.kind === 'preview' && (
        <ImportPreview
          file={stage.file}
          currentCount={currentCount ?? 0}
          confirmText={confirmText}
          onConfirmTextChange={setConfirmText}
          onConfirm={() => void handleConfirmedImport()}
          onCancel={reset}
        />
      )}

      {stage.kind === 'importing' && (
        <p role="status" style={{ marginTop: 'var(--space-2)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          Importing…
        </p>
      )}

      {stage.kind === 'done' && (
        <div role="status" style={{ marginTop: 'var(--space-2)' }}>
          <p
            style={{
              fontSize: 'var(--type-caption-size)',
              fontWeight: 500,
              color: 'var(--state-confirm)',
            }}
          >
            Imported. Your history, levels, and badges are rebuilt.
          </p>
          {/*
            Transparency addendum (ADR-0004, 2026-07-03): a local
            clear-then-replay makes this device match the file exactly right
            now, but sync has no delete propagation -- if this device syncs
            again later, anything the file didn't have can come back from
            the server/other devices. Calm and factual, not a warning: this
            mirrors the project's other radical-transparency surfaces (all
            XP math visible, every badge shows its criterion).
          */}
          <p
            style={{
              marginTop: 'var(--space-1)',
              fontSize: 'var(--type-caption-size)',
              color: 'var(--text-secondary)',
            }}
          >
            This device now matches the file. If it syncs again later, anything the file didn&apos;t have could
            reappear from other devices or the server.
          </p>
        </div>
      )}
    </div>
  );
}

function ImportPreview({
  file,
  currentCount,
  confirmText,
  onConfirmTextChange,
  onConfirm,
  onCancel,
}: {
  readonly file: ExportFile;
  readonly currentCount: number;
  readonly confirmText: string;
  readonly onConfirmTextChange: (value: string) => void;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
}) {
  const dates = file.events.map((e) => e.occurredOn).sort();
  const earliest = dates[0];
  const latest = dates[dates.length - 1];
  const rangeLabel = earliest && latest ? `${formatShortDate(earliest)} – ${formatShortDate(latest)}` : 'no dated events';

  const canConfirm = confirmText === 'replace';

  return (
    <div style={{ marginTop: 'var(--space-4)' }}>
      <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>
        This file contains {file.events.length} events from {rangeLabel}.
      </p>

      <div
        style={{
          marginTop: 'var(--space-4)',
          padding: 'var(--space-4)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--state-caution)',
          background: 'color-mix(in srgb, var(--state-caution) 12%, var(--surface-raised))',
        }}
      >
        <p style={{ margin: 0, fontWeight: 600, fontSize: 'var(--type-body-size)', color: 'var(--text-primary)' }}>
          Importing replaces what&apos;s here now.
        </p>
        <p style={{ marginTop: 'var(--space-1)', fontSize: 'var(--type-caption-size)', color: 'var(--text-secondary)' }}>
          Current data: {currentCount} events. To continue, type <strong>replace</strong> below.
        </p>

        <label style={{ display: 'block', marginTop: 'var(--space-3)' }}>
          <span style={{ display: 'block', fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginBottom: 'var(--space-1)' }}>
            Type &quot;replace&quot; to confirm
          </span>
          <input
            type="text"
            value={confirmText}
            onChange={(e) => onConfirmTextChange(e.target.value)}
            aria-label="Type replace to confirm import"
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-3)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-strong)',
              background: 'var(--surface-base)',
              color: 'var(--text-primary)',
              fontSize: 'var(--type-body-size)',
              width: '100%',
              maxWidth: '240px',
            }}
          />
        </label>

        <div style={{ display: 'flex', gap: 'var(--space-3)', marginTop: 'var(--space-4)' }}>
          <button
            type="button"
            disabled={!canConfirm}
            onClick={onConfirm}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-md)',
              background: canConfirm ? 'var(--state-caution)' : 'var(--surface-hover)',
              color: canConfirm ? 'var(--text-on-accent)' : 'var(--text-disabled)',
              fontWeight: 600,
              fontSize: 'var(--type-body-size)',
              border: '1px solid ' + (canConfirm ? 'var(--state-caution)' : 'var(--border-subtle)'),
              cursor: canConfirm ? 'pointer' : 'not-allowed',
            }}
          >
            Import and replace
          </button>
          <button
            type="button"
            onClick={onCancel}
            style={{
              minHeight: 'var(--touch-target-min)',
              padding: '0 var(--space-5)',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border-strong)',
              color: 'var(--text-secondary)',
              fontWeight: 500,
              fontSize: 'var(--type-body-size)',
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Reads a picked `File`'s text content via `FileReader` rather than the
 * newer `Blob.prototype.text()` — jsdom (the component-test environment)
 * does not implement `Blob.text()`/`arrayBuffer()` as of v24, so relying on
 * it would make this path untestable without a browser; `FileReader` is
 * broadly supported (real browsers and jsdom alike) and is the standard
 * mechanism for exactly this "read a picked file's contents" case.
 */
function readFileAsText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      resolve(typeof result === 'string' ? result : '');
    };
    reader.onerror = () => reject(new Error('Failed to read the picked file.'));
    reader.readAsText(file);
  });
}

/**
 * Structural validation only (IM-1: "validated... before any data
 * mutates") — checks the shape `DomainApi.importData` requires, never
 * mutates anything itself. A file that parses as JSON but isn't a Compass
 * export (wrong shape, missing fields, events not an array) is rejected
 * here before ever reaching the domain layer.
 */
function isExportFile(value: unknown): value is ExportFile {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    candidate.format === 'compass-export' &&
    candidate.formatVersion === 1 &&
    typeof candidate.exportedAt === 'string' &&
    Array.isArray(candidate.events) &&
    candidate.events.every(isPlausibleEvent)
  );
}

function isPlausibleEvent(value: unknown): boolean {
  if (typeof value !== 'object' || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.eventId === 'string' &&
    typeof candidate.type === 'string' &&
    typeof candidate.occurredOn === 'string' &&
    typeof candidate.recordedAt === 'string'
  );
}
